import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Feather, Crown, Trees, Trash2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { BARDE, DRUIDE } from "@/constants/testIds";
import { chatBarde, chatDruide, chatDruideUsage, chatDruideClear, chatBardeHistory, chatDruideHistory } from "@/lib/api";
import { useAccount } from "@/lib/account";
import { useLang } from "@/lib/lang";
import { TRACKS, PLAYLISTS, ACCENTS } from "@/lib/tracks";
import BgMusic from "@/components/BgMusic";
import MusicPicker from "@/components/MusicPicker";
import VoiceButton from "@/components/VoiceButton";
import { ChatDisclaimer } from "@/components/AiNotice";

const BARDE_SESSION_KEY = "astrovoyance.barde_session";
const DRUIDE_SESSION_KEY = "astrovoyance.druide_session";

export default function CelticChats() {
  return (
    <section className="relative py-24 sm:py-32">
      <div className="hair absolute top-0 left-0 right-0" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-12">
          <div className="overline mb-4">
            <span className="kicker-line" />
            Deux voix celtiques
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Le <span className="italic" style={{ color: "var(--gold-glow)" }}>Barde</span> et le{" "}
            <span className="italic" style={{ color: "var(--violet-glow)" }}>Druide</span>.
          </h2>
          <p className="mt-4" style={{ color: "var(--violet-soft)" }}>
            Deux compagnons qui refusent de prédire — et vous forment à votre autonomie
            avec humour, satire et sagesse celtique ancienne.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <BardeChat />
          <DruideChat />
        </div>
      </div>
    </section>
  );
}

export function BardeChat() {
  const { lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(BARDE_SESSION_KEY) || "");
  const boxRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    chatBardeHistory(sessionId)
      .then((d) => setMessages(d.messages || []))
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const my = { sender: "user", message: input.trim(), created_at: new Date().toISOString() };
    setMessages((m) => [...m, my]);
    setInput("");
    setLoading(true);
    try {
      const r = await chatBarde(my.message, sessionId, null, lang);
      if (r.session_id && r.session_id !== sessionId) {
        setSessionId(r.session_id);
        localStorage.setItem(BARDE_SESSION_KEY, r.session_id);
      }
      setMessages((m) => [...m, { sender: "advisor", message: r.reply, created_at: new Date().toISOString() }]);
    } catch {
      toast.error("Le Barde s'est endormi près du feu. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      id="barde"
      data-testid={BARDE.section}
      className="mystic-card gold-border rounded-3xl p-6 sm:p-8 flex flex-col"
      style={{ borderColor: "rgba(212, 175, 55, 0.35)" }}
    >
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, #f5d572, #a67bff)" }}
        >
          <Feather size={20} style={{ color: "var(--void)" }} />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg uppercase tracking-widest text-gold-glow">
            Le Barde Celtique
          </div>
          <div className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--muted)" }}>
            Gratuit · Anonyme · Illimité · Taquin
          </div>
        </div>
        <MusicPicker playlist={PLAYLISTS.barde} accent={ACCENTS.barde} testId="barde-music" persona="Barde" />
      </div>

      <div
        ref={boxRef}
        className="h-[380px] overflow-y-auto flex flex-col gap-3 pr-2 flex-1"
        style={{ scrollbarGutter: "stable" }}
      >
        {messages.length === 0 && (
          <div className="text-center my-auto py-6">
            <Feather className="mx-auto mb-4 sparkle" style={{ color: "var(--gold-glow)" }} size={28} />
            <p className="font-serif text-lg italic" style={{ color: "var(--violet-soft)" }}>
              « Salut, âme errante. Assieds-toi près du feu — aucun nom, aucun courriel à donner. Parle, et je te répondrai par des questions. »
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={BARDE.message(i)} className={`flex flex-col ${m.sender === "user" ? "items-end" : "items-start"}`}>
            <div className={`bubble ${m.sender === "user" ? "bubble-user" : "bubble-advisor"} ${m.sender === "user" ? "whitespace-pre-wrap" : "chat-md"}`}>
              {m.sender === "user" ? m.message : <ReactMarkdown>{m.message}</ReactMarkdown>}
            </div>
            {m.sender === "advisor" && (
              <div className="mt-1.5">
                <VoiceButton text={m.message} lang={lang} accent="#f5d572" testId={`barde-voice-${i}`} />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bubble bubble-advisor flex items-center gap-2 opacity-70">
              <Loader2 size={14} className="animate-spin" />
              Le Barde accorde sa harpe…
            </div>
          </div>
        )}
      </div>

      <form onSubmit={send} className="mt-4 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          data-testid={BARDE.input}
          placeholder="Parlez au Barde…"
          className="mystic-input flex-1"
        />
        <button type="submit" disabled={loading} data-testid={BARDE.send} className="btn-gold shrink-0 !py-3 !px-5 disabled:opacity-60">
          <Send size={14} />
        </button>
      </form>
      <ChatDisclaimer testId="barde-ai-disclaimer" />
    </div>
  );
}

export function DruideChat() {
  const { email, status } = useAccount();
  const { lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(DRUIDE_SESSION_KEY) || "");
  const [usage, setUsage] = useState({ used_today: 0, limit: null, premium_active: true, remaining: null });
  const boxRef = useRef(null);

  // Load history when we have a session (anonymous) OR an email
  useEffect(() => {
    if (!sessionId && !email) return;
    chatDruideHistory(sessionId, email)
      .then((d) => {
        setMessages(d.messages || []);
        if (d.session_id && d.session_id !== sessionId) {
          setSessionId(d.session_id);
          localStorage.setItem(DRUIDE_SESSION_KEY, d.session_id);
        }
      })
      .catch(() => {});
  }, [sessionId, email]);

  // Refresh usage counter
  useEffect(() => {
    chatDruideUsage(email)
      .then((r) => setUsage(r))
      .catch(() => {});
  }, [email, status?.premium_active, messages.length]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const my = { sender: "user", message: input.trim(), created_at: new Date().toISOString() };
    setMessages((m) => [...m, my]);
    setInput("");
    setLoading(true);
    try {
      const r = await chatDruide(my.message, sessionId, email, lang);
      if (r.session_id && r.session_id !== sessionId) {
        setSessionId(r.session_id);
        localStorage.setItem(DRUIDE_SESSION_KEY, r.session_id);
      }
      setMessages((m) => [...m, { sender: "advisor", message: r.reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail?.code === "daily_druide_limit_reached") {
        toast.error(lang === "fr"
          ? `Le Druide t'a offert ${usage.limit} conseils aujourd'hui — passe Premium pour continuer.`
          : `The Druid offered you ${usage.limit} counsels today — go Premium to continue.`);
        document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
      } else {
        toast.error(lang === "fr" ? "Le Druide médite. Réessayez." : "The Druid is meditating. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const wipeAndLeave = async () => {
    try { await chatDruideClear(sessionId); } catch { /* ignore */ }
    localStorage.removeItem(DRUIDE_SESSION_KEY);
    setSessionId("");
    setMessages([]);
    toast.success(lang === "fr" ? "Session Druide effacée." : "Druid session wiped.");
  };

  const isPremium = usage.premium_active || status?.premium_active;
  const remaining = isPremium ? null : (usage.remaining ?? Math.max(usage.limit - usage.used_today, 0));
  const limitReached = !isPremium && remaining <= 0;

  return (
    <div
      id="druide"
      data-testid={DRUIDE.section}
      className="mystic-card gold-border rounded-3xl p-6 sm:p-8 flex flex-col"
      style={{ borderColor: "rgba(166, 123, 255, 0.5)" }}
    >
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, #a67bff, #d4af37)" }}
        >
          <Trees size={20} style={{ color: "var(--void)" }} />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg uppercase tracking-widest" style={{ color: "var(--violet-glow)" }}>
            Le Druide numérique
          </div>
          <div className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--muted)" }}>
            {isPremium
              ? (lang === "fr" ? "Gratuit · Illimité · Tuteur érudit" : "Free · Unlimited · Erudite tutor")
              : (lang === "fr"
                  ? `Freemium · ${usage.used_today}/${usage.limit} conseils aujourd'hui`
                  : `Freemium · ${usage.used_today}/${usage.limit} counsels today`)}
          </div>
        </div>
        <MusicPicker playlist={PLAYLISTS.druide} accent={ACCENTS.druide} testId="druide-music" persona="Druide" />
      </div>

      <div
        ref={boxRef}
        className="h-[380px] overflow-y-auto flex flex-col gap-3 pr-2 flex-1"
      >
        {messages.length === 0 && (
          <div className="text-center my-auto py-6">
            <Trees className="mx-auto mb-4" style={{ color: "var(--violet-glow)" }} size={28} />
            <p className="font-serif text-lg italic" style={{ color: "var(--violet-soft)" }}>
              {lang === "fr"
                ? "« Bienvenue dans le Nemeton. Ici, je n'oracle rien — je te forme. Pose une question de vie. »"
                : "\"Welcome to the Nemeton. Here, I do not oracle — I train you. Ask a life question.\""}
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={DRUIDE.message(i)} className={`flex flex-col ${m.sender === "user" ? "items-end" : "items-start"}`}>
            <div className={`bubble ${m.sender === "user" ? "bubble-user" : "bubble-advisor"} ${m.sender === "user" ? "whitespace-pre-wrap" : "chat-md"}`}>
              {m.sender === "user" ? m.message : <ReactMarkdown>{m.message}</ReactMarkdown>}
            </div>
            {m.sender === "advisor" && (
              <div className="mt-1.5">
                <VoiceButton text={m.message} lang={lang} accent="#a67bff" testId={`druide-voice-${i}`} />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bubble bubble-advisor flex items-center gap-2 opacity-70">
              <Loader2 size={14} className="animate-spin" />
              {lang === "fr" ? "Le Druide consulte le grand chêne…" : "The Druid consults the great oak…"}
            </div>
          </div>
        )}
      </div>

      {limitReached ? (
        <div
          data-testid={DRUIDE.upgrade}
          className="mt-4 p-4 rounded-2xl flex items-center gap-3"
          style={{ background: "rgba(212, 175, 55, 0.08)", border: "1px solid rgba(212, 175, 55, 0.3)" }}
        >
          <Crown size={16} className="text-gold shrink-0" />
          <span className="text-sm" style={{ color: "var(--champagne)" }}>
            {lang === "fr"
              ? <>Les {usage.limit} conseils gratuits sont épuisés. Passe <a href="/#pricing" className="underline" style={{ color: "var(--gold-glow)" }}>Premium à 15 $/mois</a> pour un accès illimité.</>
              : <>The {usage.limit} free counsels are exhausted. Go <a href="/#pricing" className="underline" style={{ color: "var(--gold-glow)" }}>Premium at $15/mo</a> for unlimited access.</>}
          </span>
        </div>
      ) : (
        <form onSubmit={send} className="mt-4 flex gap-2 items-stretch">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            data-testid={DRUIDE.input}
            placeholder={lang === "fr" ? "Pose une question au Druide…" : "Ask the Druid a question…"}
            className="mystic-input flex-1"
            disabled={loading}
          />
          <button type="submit" disabled={loading || !input.trim()} data-testid={DRUIDE.send} className="btn-gold shrink-0 !py-3 !px-5 disabled:opacity-60">
            <Send size={14} />
          </button>
          {sessionId && (
            <button type="button" onClick={wipeAndLeave}
                    className="shrink-0 !py-3 !px-3 rounded-xl text-xs font-mono"
                    style={{ background: "rgba(180,40,40,0.15)", border: "1px solid rgba(255,120,120,0.3)", color: "#ffb8a8" }}
                    title={lang === "fr" ? "Effacer" : "Wipe"}>
              <Trash2 size={14} />
            </button>
          )}
        </form>
      )}
      <ChatDisclaimer testId="druide-ai-disclaimer" />
    </div>
  );
}
